import React, { useMemo, useState } from 'react';

import { Button, Container, Grid, MenuItem, TextField, Typography, makeStyles } from '@material-ui/core';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

import FooterButton from 'components/FooterButton';
import Table from 'components/Table';
import pocketsafeCustomersApi from 'config/api/pocketsafeCustomers/pocketsafeCustomers';
import usePagination from 'hooks/usePagination';
import customer_messages from 'messages/customer_messages';
import general_messages from 'messages/general_messages';

import PocketSafeCustomerOnboardingDialog from './_components/PocketSafeCustomerOnboardingDialog';

type CustomerMessage = readonly string[];

type CustomerRow = {
  id: number;
  organization: string;
  organization_number: string;
  owner: {
    name: string | null;
    email: string | null;
    status: 'active' | 'inactive' | 'missing';
  };
  administrators: number;
  active_users: number;
  payment_method: string;
  billing_status: string;
  created: string;
  onboarding_type: string;
  created_by: string | null;
  status: string;
};

const useStyles = makeStyles(theme => ({
  header: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    gap: theme.spacing(4),
    alignItems: 'start',
    padding: theme.spacing(5, 0, 3),
    borderBottom: `3px solid ${theme.palette.primary.light}`,
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
  },
  filters: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
}));

const PocketSafeCustomersPage = () => {
  const styles = useStyles();
  const { t } = useTranslation();
  const translate = (message: CustomerMessage) => t(message[0], message[1] || '');
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [sort, setSort] = useState<{ key: string; direction: string; internalKey: string }>({
    key: 'created_at',
    direction: 'desc',
    internalKey: 'created_at',
  });
  const [filters, setFilters] = useState<{
    status: string;
    payment_method: string;
    onboarding_type: string;
  }>({
    status: '',
    payment_method: '',
    onboarding_type: '',
  });

  const { data, getData, hasNextPage, loading, refreshData } = usePagination({
    endpointFunc: pocketsafeCustomersApi.getAll,
    isGet: true,
    search,
    sort,
    dependencies: [search, sort, filters],
    queryParams: filters,
    limits: [25, 20, 10],
  });

  const rows = (data || []) as CustomerRow[];
  const columns = useMemo(
    () => [
      { id: 'name', label: translate(customer_messages.organization), get: (row: CustomerRow) => row.organization, width: '8%' },
      {
        id: 'organization_number',
        label: translate(customer_messages.organization_number),
        get: (row: CustomerRow) => row.organization_number,
        width: '8%',
      },
      {
        id: 'owner',
        label: translate(customer_messages.owner),
        width: '16%',
        blockSorting: true,
        get: (row: CustomerRow) => {
          if (!row.owner?.name) return translate(customer_messages.owner_missing);
          const statusLabel =
            row.owner.status === 'inactive' ? translate(customer_messages.owner_inactive) : translate(customer_messages.owner_active);
          return `${row.owner.name} (${row.owner.email}) - ${statusLabel}`;
        },
      },
      {
        id: 'administrators',
        label: translate(customer_messages.administrators),
        get: (row: CustomerRow) => row.administrators,
        width: '8%',
      },
      { id: 'active_users', label: translate(customer_messages.active_users), get: (row: CustomerRow) => row.active_users, width: '4%' },
      {
        id: 'payment_method',
        blockSorting: true,
        label: translate(customer_messages.payment_method),
        get: (row: CustomerRow) => row.payment_method,
        width: '9%',
      },
      {
        id: 'billing_status',
        blockSorting: true,
        label: translate(customer_messages.billing_status),
        get: (row: CustomerRow) => row.billing_status,
        width: '10%',
      },
      {
        id: 'created_at',
        label: translate(customer_messages.created),
        get: (row: CustomerRow) => format(new Date(row.created), 'yyyy-MM-dd HH:mm'),
        width: '10%',
      },
      {
        id: 'onboarding_type',
        label: translate(customer_messages.onboarding_type),
        get: (row: CustomerRow) => row.onboarding_type,
        width: '9%',
      },
      {
        id: 'created_by',
        blockSorting: true,
        label: translate(customer_messages.created_by),
        get: (row: CustomerRow) => row.created_by,
        width: '10%',
      },
      {
        id: 'status',
        blockSorting: true,
        label: translate(customer_messages.status),
        get: (row: CustomerRow) => row.status,
        width: '8%',
      },
    ],
    [t],
  );

  return (
    <>
      <Container>
        <header className={styles.header}>
          <div>
            <Typography component='h1' variant='h2'>
              {translate(customer_messages.title)}
            </Typography>
            <Typography variant='body1'>{translate(customer_messages.description)}</Typography>
          </div>
          <div className={styles.actions}>
            <Button color='primary' onClick={() => pocketsafeCustomersApi.exportCsv({ search, ...filters })} variant='outlined'>
              {translate(customer_messages.export_csv)}
            </Button>
            <Button color='primary' onClick={() => pocketsafeCustomersApi.exportInvoiceCsv({ search, ...filters })} variant='outlined'>
              {translate(customer_messages.export_invoice_csv)}
            </Button>
            <Button color='secondary' onClick={() => setDialogOpen(true)} variant='contained'>
              {translate(customer_messages.onboard_button)}
            </Button>
          </div>
        </header>

        <Grid className={styles.filters} container spacing={2}>
          <Grid item md={4} sm={6} xs={12}>
            <TextField
              fullWidth
              label={translate(customer_messages.organization)}
              onChange={event => setSearch(event.target.value)}
              size='small'
              value={search}
              variant='outlined'
            />
          </Grid>
          <Grid item md={3} sm={6} xs={12}>
            <TextField
              fullWidth
              label={translate(customer_messages.filters_status)}
              onChange={event => setFilters(prev => ({ ...prev, status: event.target.value }))}
              select
              size='small'
              value={filters.status}
              variant='outlined'
            >
              <MenuItem value=''>{translate(customer_messages.all)}</MenuItem>
              <MenuItem value='active'>{translate(customer_messages.active)}</MenuItem>
              <MenuItem value='archived'>{translate(customer_messages.archived)}</MenuItem>
            </TextField>
          </Grid>
          <Grid item md={3} sm={6} xs={12}>
            <TextField
              fullWidth
              label={translate(customer_messages.filters_payment_method)}
              onChange={event => setFilters(prev => ({ ...prev, payment_method: event.target.value }))}
              select
              size='small'
              value={filters.payment_method}
              variant='outlined'
            >
              <MenuItem value=''>{translate(customer_messages.all)}</MenuItem>
              <MenuItem value='invoice'>{translate(customer_messages.invoice)}</MenuItem>
              <MenuItem value='stripe'>{translate(customer_messages.stripe)}</MenuItem>
            </TextField>
          </Grid>
          <Grid item md={2} sm={6} xs={12}>
            <TextField
              fullWidth
              label={translate(customer_messages.filters_onboarding_type)}
              onChange={event => setFilters(prev => ({ ...prev, onboarding_type: event.target.value }))}
              select
              size='small'
              value={filters.onboarding_type}
              variant='outlined'
            >
              <MenuItem value=''>{translate(customer_messages.all)}</MenuItem>
              <MenuItem value='internal'>{translate(customer_messages.internal)}</MenuItem>
              <MenuItem value='self_service'>{translate(customer_messages.self_service)}</MenuItem>
            </TextField>
          </Grid>
        </Grid>

        <Table
          clickableRow={false}
          data={rows}
          dataMapping={columns}
          enableSearch={false}
          onSort={(key: string, direction: string) => setSort({ key, direction, internalKey: key })}
          refetching={loading}
          sort={sort}
        />
        {hasNextPage && (
          <FooterButton className='' loading={loading} onClick={getData}>
            {translate(general_messages.show_more)}
          </FooterButton>
        )}
      </Container>

      {dialogOpen && (
        <PocketSafeCustomerOnboardingDialog
          onClose={() => setDialogOpen(false)}
          onCreated={() => {
            setDialogOpen(false);
            refreshData();
          }}
          open={dialogOpen}
        />
      )}
    </>
  );
};

export default PocketSafeCustomersPage;
